import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mic, MicOff, ArrowLeft, Volume2, Bluetooth, RefreshCw, AlertTriangle, Smartphone, Info } from 'lucide-react';

function MKVoice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.es_pro || user?.role === 'entrenador';

  const [status, setStatus] = useState('idle'); // idle | live | error
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('default');
  const [gain, setGain] = useState(1.5);
  const [volume, setVolume] = useState(0);
  const [noiseSuppress, setNoiseSuppress] = useState(true);
  const [showDevices, setShowDevices] = useState(false);
  const [sinkSupported, setSinkSupported] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [wakeLock, setWakeLock] = useState(null);

  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const gainNodeRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    // Check setSinkId support
    const test = new (window.AudioContext || window.webkitAudioContext)();
    setSinkSupported(typeof test.setSinkId === 'function');
    test.close();
    loadDevices();
    return () => stopTransmission();
  }, []);

  const loadDevices = async () => {
    try {
      // Need mic permission first to see full device labels
      await navigator.mediaDevices.getUserMedia({ audio: true }).then(s => s.getTracks().forEach(t => t.stop()));
      const all = await navigator.mediaDevices.enumerateDevices();
      const outputs = all.filter(d => d.kind === 'audiooutput');
      setDevices(outputs);
    } catch (e) {
      console.error('Error listing devices:', e);
      setDevices([]);
    }
  };

  const startTransmission = async () => {
    try {
      setErrorMsg('');
      setStatus('live');

      // Request wake lock to keep screen on
      try {
        if ('wakeLock' in navigator) {
          const wl = await navigator.wakeLock.request('screen');
          setWakeLock(wl);
        }
      } catch (e) { /* non-critical */ }

      // Get microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: noiseSuppress,
          autoGainControl: false,
          channelCount: 1,
          sampleRate: 48000
        }
      });
      streamRef.current = stream;

      // Create AudioContext
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx({ sampleRate: 48000, latencyHint: 'interactive' });

      // Set output device (setSinkId) - Chrome 110+
      if (sinkSupported && selectedDevice !== 'default') {
        try {
          await ctx.setSinkId(selectedDevice);
        } catch (e) {
          console.warn('setSinkId failed:', e);
        }
      }

      // Build audio graph: mic → gain → analyser → destination (speaker)
      const source = ctx.createMediaStreamSource(stream);
      const gainNode = ctx.createGain();
      gainNode.gain.value = gain;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;

      source.connect(gainNode);
      gainNode.connect(analyser);
      analyser.connect(ctx.destination);

      audioCtxRef.current = ctx;
      gainNodeRef.current = gainNode;
      analyserRef.current = analyser;

      // Start VU meter
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateVolume = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setVolume(avg / 255);
        rafRef.current = requestAnimationFrame(updateVolume);
      };
      updateVolume();

    } catch (e) {
      console.error('Start error:', e);
      setErrorMsg(e.message || 'Error al acceder al microfono');
      setStatus('error');
    }
  };

  const stopTransmission = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    analyserRef.current = null;
    gainNodeRef.current = null;
    setVolume(0);
    setStatus('idle');
    if (wakeLock) { wakeLock.release(); setWakeLock(null); }
  }, [wakeLock]);

  const updateGain = (val) => {
    setGain(val);
    if (gainNodeRef.current) gainNodeRef.current.gain.value = val;
  };

  if (!isPro) {
    if (user) navigate('/');
    return null;
  }

  const isLive = status === 'live';
  const vuPercent = Math.min(volume * 100, 100);
  const vuColor = vuPercent > 80 ? 'bg-red-500' : vuPercent > 50 ? 'bg-yellow-500' : 'bg-green-500';

  // Group devices by type
  const btDevices = devices.filter(d => d.label.toLowerCase().includes('bluetooth') || d.label.toLowerCase().includes('bt'));
  const otherDevices = devices.filter(d => !d.label.toLowerCase().includes('bluetooth') && !d.label.toLowerCase().includes('bt') && d.deviceId !== 'default');
  const selectedLabel = devices.find(d => d.deviceId === selectedDevice)?.label || 'Predeterminado del sistema';

  return (
    <div className="min-h-screen bg-dark-mk p-4 flex flex-col items-center" data-testid="mk-voice-page">
      <div className="w-full max-w-md space-y-4 mt-2">

        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white p-0" data-testid="btn-back">
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-600'}`} />
            <span className="text-gold font-bold uppercase tracking-wider text-lg">MK Voice</span>
          </div>
          <div className="w-6" />
        </div>

        {/* iOS Warning */}
        {!sinkSupported && (
          <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-xl p-3 flex gap-3" data-testid="ios-warning">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-yellow-400 font-medium">iOS / Safari detectado</p>
              <p className="text-yellow-500/70 text-xs mt-1">No se puede elegir altavoz Bluetooth desde el navegador. Conecta el altavoz desde Ajustes &gt; Bluetooth de tu iPhone. El audio saldra automaticamente por el dispositivo conectado.</p>
            </div>
          </div>
        )}

        {/* Main Card */}
        <Card className={`border shadow-2xl relative overflow-hidden transition-all duration-500 ${isLive ? 'bg-gray-900 border-green-500/40 shadow-green-500/10' : 'bg-gray-900 border-gray-800'}`}>
          <CardContent className="p-6 flex flex-col items-center text-center space-y-6">

            {/* Mic button / VU ring */}
            <div className="relative">
              <div className={`w-40 h-40 rounded-full flex items-center justify-center border-4 transition-all duration-300 ${isLive ? 'border-green-500' : 'border-gray-700'}`}
                style={isLive ? { boxShadow: `0 0 ${20 + vuPercent * 0.6}px rgba(34,197,94,${0.2 + vuPercent * 0.005})` } : {}}>
                {/* VU meter ring */}
                {isLive && (
                  <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 160 160">
                    <circle cx="80" cy="80" r="74" fill="none" stroke="#1f2937" strokeWidth="6" />
                    <circle cx="80" cy="80" r="74" fill="none" stroke={vuPercent > 80 ? '#ef4444' : vuPercent > 50 ? '#eab308' : '#22c55e'}
                      strokeWidth="6" strokeLinecap="round"
                      strokeDasharray={`${vuPercent * 4.65} 465`}
                      className="transition-all duration-75" />
                  </svg>
                )}
                <button onClick={isLive ? stopTransmission : startTransmission}
                  className={`w-28 h-28 rounded-full flex items-center justify-center transition-all active:scale-95 z-10 ${isLive ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-800 hover:bg-gray-700 border border-gray-600'}`}
                  data-testid="btn-mic-toggle">
                  {isLive
                    ? <Mic className="w-12 h-12 text-white" />
                    : <MicOff className="w-12 h-12 text-gray-400" />
                  }
                </button>
              </div>
            </div>

            {/* Status */}
            <div>
              <h2 className={`text-2xl font-black ${isLive ? 'text-green-400' : 'text-white'}`} data-testid="status-text">
                {isLive ? 'TRANSMITIENDO' : 'INACTIVO'}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                {isLive ? 'Tu voz sale por el altavoz' : 'Pulsa el boton para empezar'}
              </p>
            </div>

            {/* VU bar */}
            {isLive && (
              <div className="w-full" data-testid="vu-meter">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500 text-[10px]">NIVEL</span>
                  <span className="text-gray-400 text-xs font-mono">{Math.round(vuPercent)}%</span>
                </div>
                <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-75 ${vuColor}`}
                    style={{ width: `${vuPercent}%` }} />
                </div>
              </div>
            )}

            {/* Error */}
            {errorMsg && (
              <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-3 w-full">
                <p className="text-red-400 text-sm">{errorMsg}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gain Control */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm flex items-center gap-2"><Volume2 className="w-4 h-4" /> Ganancia del micro</span>
              <span className="text-white font-mono text-sm">{gain.toFixed(1)}x</span>
            </div>
            <input type="range" min="0.5" max="3" step="0.1" value={gain}
              onChange={e => updateGain(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold"
              data-testid="gain-slider" />
            <div className="flex justify-between text-[10px] text-gray-600 mt-1">
              <span>Bajo</span><span>Normal</span><span>Alto</span>
            </div>
          </CardContent>
        </Card>

        {/* Noise suppression toggle */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Reduccion de ruido</span>
              <button onClick={() => !isLive && setNoiseSuppress(!noiseSuppress)}
                className={`w-12 h-6 rounded-full transition-colors relative ${noiseSuppress ? 'bg-green-600' : 'bg-gray-700'} ${isLive ? 'opacity-50' : ''}`}
                disabled={isLive} data-testid="toggle-noise">
                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${noiseSuppress ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <p className="text-gray-600 text-xs mt-1">Filtra ruido de fondo (pesas, musica ambiente)</p>
          </CardContent>
        </Card>

        {/* Device selector */}
        {sinkSupported && (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4">
              <button onClick={() => { setShowDevices(!showDevices); loadDevices(); }}
                className="w-full flex items-center justify-between" data-testid="btn-show-devices">
                <div className="flex items-center gap-2">
                  <Bluetooth className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-400 text-sm">Salida de audio</span>
                </div>
                <span className="text-white text-sm truncate max-w-[180px]">{selectedLabel}</span>
              </button>
              {showDevices && (
                <div className="mt-3 space-y-1" data-testid="device-list">
                  {/* Default */}
                  <button onClick={() => { setSelectedDevice('default'); setShowDevices(false); }}
                    className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2 transition-colors ${selectedDevice === 'default' ? 'bg-gold/10 border border-gold/30' : 'hover:bg-gray-800'}`}
                    data-testid="device-default">
                    <Smartphone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-white">Predeterminado del sistema</span>
                  </button>
                  {/* Bluetooth devices */}
                  {btDevices.length > 0 && (
                    <p className="text-blue-400 text-[10px] uppercase tracking-wider pt-2 px-2 font-medium">Bluetooth</p>
                  )}
                  {btDevices.map(d => (
                    <button key={d.deviceId} onClick={() => { setSelectedDevice(d.deviceId); setShowDevices(false); }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2 transition-colors ${selectedDevice === d.deviceId ? 'bg-blue-500/10 border border-blue-500/30' : 'hover:bg-gray-800'}`}
                      data-testid={`device-${d.deviceId}`}>
                      <Bluetooth className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-white truncate">{d.label || 'Altavoz Bluetooth'}</span>
                    </button>
                  ))}
                  {/* Other devices */}
                  {otherDevices.length > 0 && (
                    <p className="text-gray-500 text-[10px] uppercase tracking-wider pt-2 px-2 font-medium">Otros</p>
                  )}
                  {otherDevices.map(d => (
                    <button key={d.deviceId} onClick={() => { setSelectedDevice(d.deviceId); setShowDevices(false); }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2 transition-colors ${selectedDevice === d.deviceId ? 'bg-gold/10 border border-gold/30' : 'hover:bg-gray-800'}`}
                      data-testid={`device-${d.deviceId}`}>
                      <Volume2 className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-white truncate">{d.label || 'Dispositivo de audio'}</span>
                    </button>
                  ))}
                  {devices.length <= 1 && (
                    <p className="text-gray-600 text-xs text-center py-3">No se detectan altavoces externos. Vincula uno por Bluetooth en los Ajustes del dispositivo.</p>
                  )}
                  <button onClick={loadDevices} className="w-full py-2 text-xs text-gray-500 hover:text-gold flex items-center justify-center gap-1" data-testid="btn-refresh-devices">
                    <RefreshCw className="w-3 h-3" /> Actualizar lista
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info box */}
        <div className="bg-gray-800/50 rounded-xl p-4 text-sm text-gray-400 flex gap-3">
          <Info className="h-5 w-5 shrink-0 text-gold mt-0.5" />
          <div>
            <p className="text-gray-300 font-medium mb-1">Como usar</p>
            <ol className="text-xs space-y-1 text-gray-500 list-decimal list-inside">
              <li>Conecta tu altavoz Bluetooth desde Ajustes del movil</li>
              <li>Selecciona el altavoz en "Salida de audio" arriba</li>
              <li>Pulsa el boton del microfono</li>
              <li>Habla — tu voz sale por el altavoz</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MKVoice;
